import bbtVersion from '@bbtfe/version';
import bbtNative from '@bbtfe/native';

const loadPromises = {};

export function loadScript(src, opts = {}) {
  if (loadPromises[src]) {
    return loadPromises[src];
  }
  const promise = (loadPromises[src] = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = opts.type || 'text/javascript';
    script.charset = opts.charset || 'utf8';
    script.async = 'async' in opts ? !!opts.async : true;
    script.src = src;
    if (opts.attrs) {
      for (var attr in opts.attrs) {
        script.setAttribute(attr, opts.attrs[attr]);
      }
    }
    let loaded = false;
    script.onload = script.onreadystatechange = function () {
      if (
        (script.readyState &&
          script.readyState !== 'complete' &&
          script.readyState !== 'loaded') ||
        loaded
      ) {
        return false;
      }
      script.onload = script.onreadystatechange = null;
      loaded = true;
      resolve();
    };
    script.onerror = function () {
      script.onload = script.onreadystatechange = null;
      reject();
    };
    const head = document.getElementsByTagName('head')[0];
    head.insertBefore(script, head.firstChild);
  }));
  return promise;
}

export function loadCSSCode(code) {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.rel = 'stylesheet';
  //for Chrome Firefox Opera Safari
  style.appendChild(document.createTextNode(code));
  //for IE
  //style.styleSheet.cssText = code;
  const head = document.getElementsByTagName('head')[0];
  head.appendChild(style);
}

export function loadCSS(href) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  const head = document.getElementsByTagName('head')[0];
  head.appendChild(link);
}

export function loadImage(src) {
  if (loadPromises[src]) {
    return loadPromises[src];
  }
  const promise = (loadPromises[src] = new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.src = src;
    img.onload = function () {
      resolve(img);
    };
    img.onerror = function (e) {
      img.onload = null;
      reject(e);
    };
  }));
  return promise;
}

export function getPromise(timeout = 0) {
  return new Promise(function (resolve) {
    const timerId = window.setTimeout(function () {
      window.clearTimeout(timerId);
      resolve();
    }, timeout);
  });
}

export function imageToBase64(url, outputFormat) {
  return new Promise((resolve) => {
    let canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.height = img.height;
      canvas.width = img.width;
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL(outputFormat || 'image/png');
      resolve(dataURL);
      canvas = null;
    };
    img.src = url;
  });
}

const RegExpMap = {
  Edge: /Edge/i,
  Wechat: /MicroMessenger/,
  Weibo: /Weibo/,
  QQ: /\sQQ\//,
  MQQBrowser: /MQQBrowser/i,
  Qzone: /\sQzone\//,
  Mobile: /Mobile/,
  Android: /Android/,
  iOS: /iPhone|iPad|iPod/,
  iPhone: /iPhone/,
  isAppleDevice: /iPad|iPhone|iPod|Mac OS X/,
  isBot:
    /Baiduspider|Sogou\s\w+\sspider|Yisouspider|Googlebot|Bingbot|360Spider/,
  Tablet: /(iPad|Tablet)|(Android.*Chrome(?!.*Mobile))/,
  UC: /UCBrowser/,
  Sogou: /Sogou/,
  Qihoo: /QHBrowser/,
  Baidu: /baidubrowser/,
  BaiduApp: /baidubox/,
  Safari: /^((?!chrome|android).)*safari/i,
  GoogleBot: /Googlebot/,
  DingTalk: /DingTalk/, // 钉钉
  AlipayClient: /AlipayClient/, // 支付宝
  NewsArticle: /NewsArticle/, // 今日头条
  pregnancy: /(pregnancy|AliUnionMall)/, // 宝宝树孕育
  CanEatApp: /caneat_app/, //能不能吃独立app
  MikaInCar: /mika_in_car/, //车载能不能吃功能
  lama: /lama/, // 小时光独立app
  miniProgram: /miniProgram/, // 小程序
  pregnancyHm: /pregnancy_hm/, // 宝宝树孕育鸿蒙系统
};

export const queryStringToObject = (query) => {
  let json = {};
  if (!query || typeof query !== 'string') {
    return json;
  }
  if (query.indexOf('?') == 0) {
    query = query.substr(1);
  }
  let qarr = query.split('&');
  for (let i of qarr) {
    let item = i.split('=');
    json[item[0]] = item[1];
  }
  return json;
};

export function UAParser(ua) {
  ua = ua || window.navigator.userAgent;
  const userAgent = Object.keys(RegExpMap).reduce((total, item) => {
    total[item] = RegExpMap[item].test(ua);
    return total;
  }, {});
  if (document.referrer.indexOf('nativeapp.babytree.com') > -1) {
    userAgent.pregnancy = true;
  }
  if (
    /MSIE\s(\d+)\.(\d+)/i.exec(ua) ||
    /Trident.*rv[.:\s](\d+)\.(\d+)/i.exec(ua)
  ) {
    userAgent.IE = true;
  }
  if (userAgent.pregnancy && !userAgent.iPhone && !userAgent.Android) {
    const query = queryStringToObject(window.location.search);
    const clientType = query['babytree-client-type'];
    userAgent.iPhone = clientType == 'ios' ? true : false;
    userAgent.Android = clientType == 'android' ? true : false;
  }
  if (userAgent.iPhone && window.screen.height >= 812) {
    userAgent.iPhoneX = true;
  }
  userAgent.origin = ua;
  return userAgent;
}

// https://github.com/lodash/lodash/blob/master/isObject.js
export function isObject(value) {
  const type = typeof value;
  return value != null && (type === 'object' || type === 'function');
}

// 函数防抖和节流 : https://www.jianshu.com/p/c8b86b09daf0
// https://github.com/lodash/lodash/blob/master/debounce.js
export function debounce(func, wait, options) {
  let lastArgs, lastThis, maxWait, result, timerId, lastCallTime;

  let lastInvokeTime = 0;
  let leading = false;
  let maxing = false;
  let trailing = true;

  // Bypass `requestAnimationFrame` by explicitly setting `wait=0`.
  const useRAF =
    !wait && wait !== 0 && typeof window.requestAnimationFrame === 'function';

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }
  wait = +wait || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function startTimer(pendingFunc, wait) {
    if (useRAF) {
      window.cancelAnimationFrame(timerId);
      return window.requestAnimationFrame(pendingFunc);
    }
    return setTimeout(pendingFunc, wait);
  }

  function cancelTimer(id) {
    if (useRAF) {
      return window.cancelAnimationFrame(id);
    }
    clearTimeout(id);
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = startTimer(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxing && timeSinceLastInvoke >= maxWait)
    );
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = startTimer(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      cancelTimer(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(Date.now());
  }

  function pending() {
    return timerId !== undefined;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        timerId = startTimer(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = startTimer(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  debounced.pending = pending;
  return debounced;
}

// https://github.com/lodash/lodash/blob/master/throttle.js
export function throttle(func, wait, options) {
  let leading = true;
  let trailing = true;

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }
  if (isObject(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    leading,
    trailing,
    maxWait: wait
  });
}

export const http2any = function (url) {
  return url ? url.replace(/^http[s]?[:]?/, '') : null;
};

//获取url参数; 正则获取url参数，包含hash[#]和search[?]两种通用
export function getUrlQueryByName(param) {
  const reg = new RegExp('(^|&)' + param + '=([^&]*)(&|$)');
  const r =
    window.location.search.substr(1).match(reg) ||
    window.location.hash
      .substring(window.location.hash.search(/\?/) + 1)
      .match(reg);
  if (r != null) {
    return decodeURIComponent(r[2]);
  }
}

//默认显示 MM:ss 格式 都保持两位数
export const formatTime = (timesecs) => {
  if (!(timesecs >= 0)) return '0';
  let m = Math.floor(Math.floor(timesecs) / 60);
  let s = Math.floor(Math.floor(timesecs) % 60);
  let h = Math.floor(Math.floor(m) / 60);
  m = Math.floor(Math.floor(m) % 60);
  let r = '';

  m = m < 10 ? '0' + m : m;
  s = s < 10 ? '0' + s : s;
  if (h > 0) {
    r = `${h}:${m}:${s}`;
  } else {
    r = `${m}:${s}`;
  }
  if (r.indexOf('-') > -1) {
    r = '00:00';
  }
  return r;
}

// 元素任意一部分出现在可视区域
export const isElementInViewport = el => {
  if (el) {
    const rect = el?.getBoundingClientRect();
    return (rect?.top >= 0 || (rect?.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect?.bottom >= 0))
      &&
      (rect?.left >= 0 || (rect?.right <= (window.innerWidth || document.documentElement.clientWidth) && rect?.right >= 0))
  }
  return false;
}

// 元素全部出现在可视区域
export const isAllElementInViewport = el => {
  if (el) {
    const rect = el?.getBoundingClientRect();
    return (
      rect?.top >= 0 &&
      rect?.left >= 0 &&
      rect?.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
      rect?.right <=
      (window.innerWidth || document.documentElement.clientWidth)
    );
  }
  return false;
}


/**
 * 
 * @param {number} position 预览图片的起始位置
 * @param {string} jsonArray 图片地址的数组 如：['https://www.baidu.com/img1','https://www.baidu.com/img2']
 * @returns null
 */
export const setNativePhotoListBrowse = (position, jsonArray) => {
  if (bbtVersion.less('9.18.0')) {
    return bbtNative.previewImage(jsonArray[position]);
  }
  const jsonArrayStr = JSON.stringify(jsonArray)
  if (window.Bridge && window.Bridge.postMessage) {
    window.Bridge.postMessage({
      method: 'nativePhotoListBrowse',
      params: [position, jsonArrayStr]
    });
    return;
  }
  if (
    window.nativePhotoListBrowse &&
    typeof window.nativePhotoListBrowse == 'function'
  ) {
    window.nativePhotoListBrowse(position, jsonArrayStr);
    return;
  }
  if (window.android && window.android.nativePhotoListBrowse) {
    window.android.nativePhotoListBrowse(position, jsonArrayStr);
    return;
  }
}

/**
 * px 转换为 rem
 * @param {*} px 
 * @returns 
 */
export const px2rem = (px) => {
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return `${px / rootFontSize}rem`;
};

/**
 * rem转px
 * @param {*} rem 
 * @returns 
 */
export const rem2px = (rem) => {
  return window.remCalc.rem2px(rem);
}